import React from 'react';

import { useAccountContext } from '../Data/accountContext';
import FormBase from '../FormBase';

import { getCorrespondentByUrl } from '../../../../services/TemplateService';
import MarginTypeRadioGroup from '../../../../components/Buttons/MarginTypeRadioGroup';
import Text from '../../../../components/Typography/Text';
import Link from '../../../../components/Typography/Link';
import Checkbox from '../../../../components/Misc/Checkbox';
import SelectSystemCode from '../../../../components/Dropdown/SelectSystemCode';
import MarginAgreementModal from '../../../../components/Modals/MarginAgreementModal';
import MarginDisclosureModal from '../../../../components/Modals/MarginDisclosureModal';
import {
  saveRegistrationDisclosure,
  saveRegistrationTrading,
} from '../../../../services/RegistrationService';
import { validateTrading, hasError } from '../Data/validation';

export default function FormTrading() {
  const { account, setAccount, viewOnly } = useAccountContext();

  const [selectedMarginTypes, setSelectedMarginTypes] = React.useState([]);

  const [open, setOpen] = React.useState({
    marginAgreementModal: false,
    marginDisclosureModal: false,
  });

  const [correspondentData, setCorrespondentData] = React.useState({});
  const [validation, setValidation] = React.useState({});

  const handleSave = async () => {
    const errs = validateTrading(account);
    setValidation(errs);
    if (hasError(errs)) {
      console.log('trading errs', errs);
      return { acct: account, err: true };
    }

    let copy = { ...account };
    await saveRegistrationTrading(copy);
    if (account.marginType === 'Cash') {
      copy = { ...account, marginDisclosure: false, marginAgreement: false };
      setAccount(copy);
    }

    return { acct: copy, err: false };
  };

  React.useEffect(() => {
    const fetchMarginTypes = async () => {
      try {
        let tempSelectedMarginTypes = [];
        const serverCorrespondent = await getCorrespondentByUrl();
        let correspondentResp = serverCorrespondent.correspondent;
        setCorrespondentData(correspondentResp);
        // Set selected margin types
        if (correspondentResp.isMargin && account.accountType !== 'IRA') {
          tempSelectedMarginTypes.push('Margin');
        }

        if (correspondentResp.isCash) {
          tempSelectedMarginTypes.push('Cash');
        }

        setSelectedMarginTypes(tempSelectedMarginTypes);
      } catch (err) {
        console.log(err);
      }
    };
    if (account.correspondent) {
      fetchMarginTypes();
    }

    // eslint-disable-next-line
  }, [account.correspondent]);

  const handleChange = (e, checkboxValue) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    let tradingDataCopy = { ...account };

    if (
      input.name === 'marginType' &&
      input.value === 'Margin' &&
      !account.marginAgreement
    ) {
      setOpen({ ...open, marginAgreementModal: true });
    }

    setAccount({
      ...tradingDataCopy,
      [input.name]:
        checkboxValue === true || checkboxValue === false ? checkboxValue : input.value,
    });
  };

  const handleCloseModal = async (acceptedValue) => {
    setOpen({
      marginAgreementModal: false,
      customerAgreementModal: false,
    });

    if (acceptedValue === 'disclosure accepted') {
      await saveRegistrationDisclosure('Margin Disclosure', true);
      setAccount({ ...account, marginDisclosure: true });
    }
    if (acceptedValue === 'margin agreement accepted') {
      await saveRegistrationDisclosure('Margin Agreement', true);
      setAccount({ ...account, marginAgreement: true });
    }
  };

  return (
    <FormBase
      name="trading"
      header="Trading"
      subHeader=""
      faq="Trading"
      save={handleSave}
    >
      <form className="trading-form-w" autoComplete="off">
        <MarginTypeRadioGroup
          required={true}
          error={!!validation?.marginType}
          msg={validation?.marginType}
          name="marginType"
          label="Please select an Account Type"
          value={account.marginType}
          correspondent={correspondentData}
          options={selectedMarginTypes}
          onChange={handleChange}
        />
        {account.marginType === 'Margin' && (
          <div>
            {!!validation?.marginAgreement && (
              <div className="grd-row">
                <Text
                  mt={10}
                  error={!!validation?.marginAgreement}
                  label="Please check Margin Agreement"
                  variant="subtitle1"
                />
              </div>
            )}
            <Checkbox
              mt={!!validation?.marginAgreement ? 30 : 10}
              ml={-3}
              required={true}
              labelLink={true}
              name="readMarginAgreement"
              label={
                <React.Fragment>
                  I have read and accepted the
                  <span
                    onClick={(e) => {
                      setOpen({ ...open, marginAgreementModal: true });
                      e.preventDefault();
                    }}
                  >
                    <Link ml={5} label="Margin Agreement" />.
                  </span>
                </React.Fragment>
              }
              checked={account.marginAgreement}
              onChange={handleChange}
              disabled={true}
            />
            <div className="grd-row">
              {!!validation?.marginDisclosure && (
                <Text
                  mt={10}
                  error={!!validation?.marginDisclosure}
                  label="Please check Margin Disclosure Statement"
                  variant="subtitle1"
                />
              )}
            </div>
            <Checkbox
              mt={!!validation?.marginDisclosure ? 10 : 0}
              ml={-3}
              required={true}
              labelLink={true}
              name="marginDisclosure"
              label={
                <React.Fragment>
                  I have read and accepted the
                  <span
                    onClick={(e) => {
                      setOpen({ ...open, marginDisclosureModal: true });
                      e.preventDefault();
                    }}
                  >
                    <Link ml={5} label="Margin Disclosure Statement" />.
                  </span>
                </React.Fragment>
              }
              checked={account.marginDisclosure}
              onChange={handleChange}
              disabled={true}
            />
          </div>
        )}
        <Text
          required={true}
          error={!!validation?.tradingPrivileges}
          msg={validation?.tradingPrivileges}
          mt={40}
          variant="subtitle1"
          label="Trading Privileges"
        />
        <div className="grd-row">
          <div className="grd-cell-none mr">
            <Checkbox
              ml={-3}
              name="stocks"
              label="Stocks"
              checked={account.stocks}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell-none mr">
            <Checkbox
              name="option"
              disabled={true}
              label="Option"
              checked={account.option}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <SelectSystemCode
              required
              disableBlank
              error={!!validation?.privacyConsent}
              msg={validation?.privacyConsent}
              name="privacyConsent"
              type="Privacy Consent"
              label="Privacy Consent"
              orderBy="code"
              value={account.privacyConsent || ''}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell">
            <Checkbox
              mt={30}
              name="accreditedInvestor"
              label="Accredited Investor"
              checked={account.accreditedInvestor}
              onChange={handleChange}
            />
          </div>
        </div>
      </form>
      {open.marginAgreementModal && (
        <MarginAgreementModal
          disabled={viewOnly}
          accountData={account}
          open={open.marginAgreementModal}
          onClose={handleCloseModal}
        />
      )}
      {open.marginDisclosureModal && (
        <MarginDisclosureModal
          disabled={viewOnly}
          open={open.marginDisclosureModal}
          close={handleCloseModal}
          tradingData={account}
        />
      )}
    </FormBase>
  );
}
